/**
 * Floating labels at the centroid of each active cluster.
 *
 * When `clusterBy` is set, every node carries a cluster id (a positionType,
 * moveCategory, submission type, disposition, family, or concept). This
 * component groups visible nodes by that id, computes the running centroid
 * each frame from the worker's live positions, and renders a single Html
 * label at the centroid of each group.
 *
 * Behavior parallels ProximityLabels but inverted:
 *   - one label per cluster instead of one per node
 *   - distance fade is gentler (clusters are big, you want their label
 *     visible from farther out)
 *   - no per-node hover/select interaction — clicking a cluster label is
 *     ambiguous, so labels are pointer-event:none
 *   - opacity drops when an unrelated selection is active so cluster
 *     labels don't compete with the per-node ProximityLabels of the
 *     focused neighborhood
 *
 * Hidden when clusterBy === 'none'.
 */
import { useMemo, useRef } from 'react';
import { Html } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { ClusterBy, Graph, Move, Position } from '../types/domain';
import type { ForceLayout } from './useForceLayout';
import { clusterIdFor } from '../data/cluster';
import { advantageClusterLabel } from '../ui/tag-labels';
import {
  CLUSTER_COLORS,
  clusterColorForId,
} from '../zones/palette';

// Distance band — wider than ProximityLabels because cluster labels are
// orientation tags, not detail. They should be readable from farther out.
const NEAR_DIST = 30;
const FAR_DIST = 240;
const DIM_FACTOR = 0.12;

const TMP_FWD = new THREE.Vector3();
const TMP_VEC = new THREE.Vector3();
const TMP_UP = new THREE.Vector3();
// World-space offset applied to single-node cluster labels along camera
// screen-down, so the cluster label sits below the node's own
// proximity/hub label (which renders above) and never overlaps it.
const SINGLE_NODE_BELOW_OFFSET = 2.6;

const HASHED_CLUSTER_TYPES = new Set<ClusterBy>(['family', 'concept']);

// "Pretty" label text for known taxonomy ids.
const LABEL_TEXT: Record<string, string> = {
  top: 'Top',
  bottom: 'Bottom',
  neutral: 'Neutral',
  terminal: 'Terminal',
  flux: 'Flux',
  sweep: 'Sweeps',
  submission: 'Submissions',
  pass: 'Passes',
  escape: 'Escapes',
  takedown: 'Takedowns',
  transition: 'Transitions',
  'guard-retention': 'Guard Retention',
  choke: 'Chokes',
  'joint-lock': 'Joint Locks',
  'leg-lock': 'Leg Locks',
  'neck-crank': 'Neck Cranks',
  other: 'Other',
  defensive: 'Defensive',
  offensive: 'Offensive',
  transitional: 'Transitional',
};

function prettifyClusterId(id: string, clusterBy?: ClusterBy): string {
  // Advantage is checked first and needs the axis, because `neutral`
  // means "standing/clinch" under position type and "neither player is
  // ahead" under advantage — same id, different word.
  const adv = clusterBy && advantageClusterLabel(id, clusterBy);
  if (adv) return adv;
  if (LABEL_TEXT[id]) return LABEL_TEXT[id];
  // Family / concept ids: turn 'closed-guard' → 'Closed Guard'
  return id
    .split(/[-_]/)
    .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

function clusterColor(id: string, clusterBy: ClusterBy): string {
  if (CLUSTER_COLORS[id]) return CLUSTER_COLORS[id];
  if (HASHED_CLUSTER_TYPES.has(clusterBy)) {
    return clusterColorForId(id, clusterBy as 'family' | 'concept');
  }
  return '#cad3df';
}

type Props = {
  graph: Graph;
  layout: ForceLayout;
  clusterBy: ClusterBy;
  positionsById: Map<string, Position>;
  movesById: Map<string, Move>;
  glowNodes: Set<string>;
  centerId: string;
};

export function ClusterLabels({
  graph,
  layout,
  clusterBy,
  positionsById,
  movesById,
  glowNodes,
  centerId,
}: Props) {
  // Pre-compute cluster membership: clusterId → [nodeIdx, ...]
  // Only positions+moves participate; interception nodes don't cluster.
  // The center node ("Game Over") is also excluded — it already has its
  // own prominent label via CenterAnchor and the user prefers not to
  // double up with a cluster tag on the central protagonist.
  const { clusterIds, members } = useMemo(() => {
    const map = new Map<string, number[]>();
    if (clusterBy === 'none') return { clusterIds: [], members: map };
    graph.nodes.forEach((n) => {
      if (n.kind !== 'position' && n.kind !== 'move') return;
      if (n.id === centerId) return; // skip the central protagonist
      const cid = clusterIdFor(n, clusterBy, positionsById, movesById);
      if (!cid) return;
      const idx = layout.nodeIndex.get(n.id);
      if (idx === undefined) return;
      const arr = map.get(cid);
      if (arr) arr.push(idx);
      else map.set(cid, [idx]);
    });
    // Order clusters by size (largest first) — used for label-z assignment.
    const clusterIds = [...map.keys()].sort(
      (a, b) => (map.get(b)?.length ?? 0) - (map.get(a)?.length ?? 0),
    );
    return { clusterIds, members: map };
  }, [graph, clusterBy, positionsById, movesById, layout.nodeIndex, centerId]);

  // One ref per cluster label DOM node and one per group anchor.
  const labelRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const groupRefs = useRef<Map<string, THREE.Group>>(new Map());

  useFrame(({ camera }) => {
    if (!layout.posRef.current) return;
    if (clusterBy === 'none') return;
    const pos = layout.posRef.current;
    camera.getWorldDirection(TMP_FWD);
    // Screen-up in world space — used to compute the "below" offset for
    // single-node clusters (we want the label to drop along screen-down,
    // not world-down, so it stays consistent under any camera rotation).
    TMP_UP.set(0, 1, 0).applyQuaternion(camera.quaternion);

    // Detect "active set exists" — when a node is selected/hovered, dim
    // unrelated cluster labels so the focused neighborhood reads cleanly.
    const hasActive = glowNodes.size > 0;

    for (const cid of clusterIds) {
      const ids = members.get(cid);
      if (!ids || ids.length === 0) continue;

      // Centroid + (lightweight) "this cluster has at least one focused
      // member" check.
      let cx = 0, cy = 0, cz = 0;
      let inActiveSet = false;
      for (const idx of ids) {
        cx += pos[idx * 3 + 0];
        cy += pos[idx * 3 + 1];
        cz += pos[idx * 3 + 2];
        if (hasActive) {
          const node = graph.nodes[idx];
          if (node && glowNodes.has(node.id)) inActiveSet = true;
        }
      }
      const inv = 1 / ids.length;
      cx *= inv;
      cy *= inv;
      cz *= inv;

      // Singleton clusters: the centroid IS the node, so the cluster
      // label would overlap the node's own proximity label. Push the
      // anchor along screen-DOWN by a worldspace offset so the cluster
      // label sits below the bridge ring instead of on top of the name.
      if (ids.length === 1) {
        cx -= TMP_UP.x * SINGLE_NODE_BELOW_OFFSET;
        cy -= TMP_UP.y * SINGLE_NODE_BELOW_OFFSET;
        cz -= TMP_UP.z * SINGLE_NODE_BELOW_OFFSET;
      }

      // Position the anchor at the (possibly offset) centroid.
      const g = groupRefs.current.get(cid);
      if (g) g.position.set(cx, cy, cz);

      // Camera-distance + forward-cone check.
      const dx = camera.position.x - cx;
      const dy = camera.position.y - cy;
      const dz = camera.position.z - cz;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      TMP_VEC.set(cx - camera.position.x, cy - camera.position.y, cz - camera.position.z);
      const len = TMP_VEC.length() || 1;
      const forwardDot = TMP_VEC.dot(TMP_FWD) / len;
      const forwardOk = forwardDot >= 0.1;

      const labelEl = labelRefs.current.get(cid);
      if (!labelEl) continue;

      if (!forwardOk) {
        labelEl.style.opacity = '0';
        continue;
      }
      // Distance fade: linear ramp NEAR → FAR.
      let opacity = (FAR_DIST - d) / (FAR_DIST - NEAR_DIST);
      if (opacity < 0) opacity = 0;
      if (opacity > 1) opacity = 1;
      // Dim when there's an active focused node and this cluster isn't
      // part of it. The focused cluster stays at full opacity.
      if (hasActive && !inActiveSet) opacity *= DIM_FACTOR;
      labelEl.style.opacity = opacity.toFixed(3);
    }
  });

  if (clusterBy === 'none') return null;

  return (
    <>
      {clusterIds.map((cid) => (
        <group
          key={cid}
          ref={(el) => {
            if (el) groupRefs.current.set(cid, el);
            else groupRefs.current.delete(cid);
          }}
        >
          <Html
            position={[0, 0, 0]}
            zIndexRange={[55, 0]}
            style={{ pointerEvents: 'none' }}
          >
            <div
              ref={(el) => {
                if (el) labelRefs.current.set(cid, el);
                else labelRefs.current.delete(cid);
              }}
              style={{
                // Centered on the centroid, drawn larger than node labels —
                // these are macro orientation tags. Sized between the
                // original 14px subtle treatment and the prominent
                // 19px/600 pass: 15px / 500 reads as orientation tags
                // without overwhelming the nodes they're labelling.
                transform: 'translate(-50%, -50%)',
                color: clusterColor(cid, clusterBy),
                fontSize: 15,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                fontWeight: 500,
                opacity: 0,
                textShadow:
                  '0 0 12px rgba(0,0,0,0.95), ' +
                  '0 0 4px rgba(0,0,0,0.95)',
                whiteSpace: 'nowrap',
                userSelect: 'none',
                pointerEvents: 'none',
                transition: 'opacity 200ms ease',
              }}
            >
              {prettifyClusterId(cid, clusterBy)}
            </div>
          </Html>
        </group>
      ))}
    </>
  );
}
